import ReactEcharts from 'echarts-for-react';
import { useEffect, useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { formatChartLabelValueForSelectedMeasure } from '../ChartOptions/Format/NumberFormatter';
import { ChartControlsProps } from '../../redux/ChartPoperties/ChartControlsInterface';
import {
  ChartDataFieldProps,
  ChartsMapStateToProps,
  ChartsReduxStateProps,
  FormatterValueProps,
} from './ChartsCommonInterfaces';
import {
  formatChartXAxisValue,
  formatChartYAxisValue,
} from '../ChartOptions/Format/NumberFormatter';
import { tooltipStyle } from '../Common/EChartTooltip';

import { palette } from '../..';

import {
  checkUidPresenceInCheckedMeasureForTooltip,
  limitChartData,
  shouldChartDisplayWithSecurity,
} from '../CommonFunctions/CommonFunctions';
import EmptyQuery from './EmptyQuery';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux';
import { updateHoveredItems } from '../../redux/TabTile/TabActions';
import useCombinedColorSchemes from '../../hooks/useCombinedColorSchemes';
import { messages } from '../CommonFunctions/aliases';
import { toast } from 'react-toastify';
import { compareWithLogicalOrder } from '../ChartOptions/Sort/dataOrdeingUtils';
import { SortConfiguration } from '../../redux/ChartPoperties/ChartControlsInterface';
import {
  AXIS_THRESHOLDS,
  LEGEND_THRESHOLDS,
  MARGIN_THRESHOLDS,
} from '../ChartOptions/ChartThresholds';

const HeatMap = ({
  //props
  propKey,
  graphDimension,
  chartArea,
  graphTileSize,
  colorScheme,
  fontFamily,
  softUI,

  //state
  chartControls,
  chartProperties,
}: ChartsReduxStateProps) => {
  // TODO: cant apply filters
  var chartControl: ChartControlsProps = chartControls.properties[propKey];
  let chartData: any[] = chartControl.chartData ? chartControl.chartData : [];
  const {
    selectedMeasureName,
    tabState,
    selectedTab,
    showDash,
    tooltipFormats,
    chartAxes,
    isTooltipEnabled,
    chartType,
  } = useSelector((state: RootState) => ({
    selectedMeasureName:
      state.chartControls?.properties[propKey]?.formatOptions?.dateFormats?.selectedMeasureName,
    tooltipFormats: state.chartControls?.properties[propKey]?.formatOptions?.tooltipFormats,
    chartAxes: state.chartProperties.properties[propKey]?.chartAxes,
    isTooltipEnabled:
      state.chartControls?.properties[propKey]?.formatOptions?.tooltipFormats?.isEnable,
    tabState: state.tabState,
    selectedTab: state.tabTileProps.selectedTabId,
    showDash: state.tabTileProps.showDash,
    chartType: state.chartProperties?.properties[propKey]?.chartType,
  }));
  const chartRef = useRef<any>(null);
  const chartInstanceRef = useRef<echarts.ECharts | null>(null);
  const dispatch = useDispatch();

  const [chartDataKeys, setChartDataKeys] = useState<any[]>([]);

  const mouseOverInteractivity =
    tabState.tabs[selectedTab]?.interactivity?.mouseOverInteractivity || false;

  const limitedChartData = limitChartData(chartData, 5000, chartAxes[3]?.fields[0]?.displayname);

  const processedChartData = limitedChartData.map((item) => {
    return Object.fromEntries(
      Object.entries(item).map(([key, value]) => [
        key,
        value === null
          ? '(Blank)'
          : typeof value === 'boolean'
            ? value
              ? 'True'
              : 'False'
            : value,
      ]),
    );
  });

  const xAxisName = (processedChartData[0] && Object.keys(processedChartData[0])[0]) || '';
  const yAxisName = (processedChartData[0] && Object.keys(processedChartData[0])[1]) || '';
  const sortConfigurations = chartControls.properties[propKey].sortConfigurations || [];

  const [maxValue, setMaxValue] = useState<number>(0);
  const [minValue, setMinValue] = useState<number>(0);
  useEffect(() => {
    if (chartData.length >= 1) {
      const keys = Object.keys(processedChartData[0] || {});
      setChartDataKeys(keys);

      const measureField = chartProperties.properties[propKey]?.chartAxes[3]?.fields[0];

      if (measureField) {
        const measureKey = measureField.displayname;
        let max = -Infinity;
        let min = Infinity;

        processedChartData.forEach((element: any) => {
          const val = element[measureKey];
          if (val !== null && val !== undefined && typeof val === 'number') {
            if (val > max) max = val;
            if (val < min) min = val;
          }
        });

        setMaxValue(max === -Infinity ? 0 : max);
        setMinValue(min === Infinity ? 0 : min);
      }
    }
  }, [chartData, chartProperties.properties[propKey]?.chartAxes]);
  useEffect(() => {
    if (chartData.length > 5000 && !showDash) {
      toast.warning(`${messages.chart.excessiveData} 5000 records`, {
        position: 'top-center',
        autoClose: 10000,
        toastId: 'chart-data-limit-warning',
        style: {
          top: '10px',
        },
      });
    }
  }, [chartData]);

  const allThemes = useCombinedColorSchemes();
  const chartThemes = allThemes.filter((el) => {
    if (
      !chartControl.colorScheme ||
      (chartArea === 'dashboard' && !chartControl.colorScale.manualColor)
    ) {
      return el.name === colorScheme;
    } else {
      return el.name === chartControl.colorScheme;
    }
  });
  const selectedTheme = chartThemes.length > 0 ? chartThemes[0] : allThemes[0];

  const getTopMarginForLegend = () => {
    var top = '';
    if (chartControl.legendOptions?.position?.top === 'top') {
      top = 'top';
    } else if (chartControl.legendOptions?.position?.top === 'bottom') {
      top = '99%';
    } else {
      top = '50%';
    }
    return top;
  };

  const [hoveredColumns, setHoveredColumns] = useState<Record<string, any>[]>([]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (
        !mouseOverInteractivity ||
        (tabState.tabs[selectedTab].hoveredPropkey &&
          tabState.tabs[selectedTab].hoveredPropkey === propKey)
      ) {
        return;
      }
      const chartInstance = chartInstanceRef.current;
      if (!chartInstance || chartInstance.isDisposed?.()) return;

      const index = processedChartData.findIndex((item) =>
        tabState.tabs[selectedTab].hoveredItems?.some(
          (hovered: Record<string, any>) =>
            String(hovered[chartDataKeys[0]]) === String(item[chartDataKeys[0]]),
        ),
      );
      const seriesIndex = processedChartData.findIndex((item) =>
        tabState.tabs[selectedTab].hoveredItems?.some(
          (hovered: Record<string, any>) =>
            String(hovered[chartDataKeys[1]]) === String(item[chartDataKeys[1]]),
        ),
      );
      const pointsToHighlight = processedChartData
        .map((item, index) => ({ item, index }))
        .filter(({ item }) =>
          tabState.tabs[selectedTab].hoveredItems?.some(
            (hovered: Record<string, any>) =>
              String(item[chartDataKeys[0]]) === String(hovered[chartDataKeys[0]]) ||
              String(item[chartDataKeys[1]]) === String(hovered[chartDataKeys[1]]),
          ),
        );

      if (index >= 0 && seriesIndex >= 0) {
        chartInstance.dispatchAction({
          type: 'highlight',
          seriesIndex: 0,
          dataIndex: index + seriesIndex,
        });
      } else if (index >= 0 || seriesIndex >= 0) {
        chartInstance.dispatchAction({ type: 'downplay', seriesIndex });

        pointsToHighlight.forEach(({ index }) => {
          chartInstance.dispatchAction({
            type: 'highlight',
            seriesIndex: 0,
            dataIndex: index,
          });
        });
      } else {
        chartInstance.dispatchAction({
          type: 'downplay',
          seriesIndex: 0,
        });
      }
    }, 30); // slight delay helps when charts update

    return () => clearTimeout(timeout);
  }, [tabState.tabs[selectedTab].hoveredItems, chartData]);

  const hoveredRef = useRef<Record<string, any> | null>(null);
  const hoverdTimeout = useRef<NodeJS.Timeout | null>(null);

  const handleChartReady = (chartInstance: any) => {
    if (!mouseOverInteractivity) return;

    chartInstanceRef.current = chartInstance;

    chartInstance.on('mouseover', (params: any) => {
      if (
        params?.data &&
        hoveredRef.current?.[chartDataKeys[0]] !== params.data[chartDataKeys[0]] &&
        hoveredRef.current?.[chartDataKeys[1]] !== params.data[chartDataKeys[1]]
      ) {
        if (hoverdTimeout.current) clearTimeout(hoverdTimeout.current);

        hoverdTimeout.current = setTimeout(() => {
          const newEntry_1 = {
            [chartDataKeys[0]]: params.data[chartDataKeys[0]],
          };
          const newEntry_2 = {
            [chartDataKeys[1]]: params.data[chartDataKeys[1]],
          };
          hoveredRef.current = newEntry_1;

          setHoveredColumns((prev) => {
            const isDuplicate = prev.some(
              (item) => item[chartDataKeys[0]] === params.data[chartDataKeys[0]],
            );
            if (!isDuplicate) {
              return [...prev, newEntry_1, newEntry_2];
            }
            return prev;
          });
        }, 200);
      }
    });

    chartInstance.on('mouseout', (params: any) => {
      if (hoverdTimeout.current) clearTimeout(hoverdTimeout.current);
      if (params?.seriesName) {
        hoveredRef.current = null;
        setHoveredColumns([]);
      }
    });
  };

  useEffect(() => {
    dispatch(updateHoveredItems(selectedTab, hoveredColumns, propKey));
  }, [hoveredColumns]);
  const isValidNumber = (v: any) => {
    return v !== '' && v !== null && v !== undefined && !isNaN(Number(v));
  };

  // Helper function to get unique sorted values for an axis
  const getSortedAxisValues = (data: any[], fieldName: string, sortConfig?: SortConfiguration) => {
    const uniqueValues = [...new Set(data.map((item) => item[fieldName]))];

    if (!sortConfig || !sortConfig.sortOrder) {
      // Default ascending sort if no config
      return uniqueValues.sort((a, b) => compareWithLogicalOrder(a, b, true));
    }

    if (sortConfig.sortOrder === 'Manual Sort') {
      if (sortConfig.manualSortOrderObject) {
        // Use manual sort order
        return uniqueValues.sort((a, b) => {
          const orderA = sortConfig.manualSortOrderObject?.[a] ?? 999999;
          const orderB = sortConfig.manualSortOrderObject?.[b] ?? 999999;
          return orderA - orderB;
        });
      } else {
        // Sorting in ascending order if no manual sort order is available
        return uniqueValues.sort((a, b) => compareWithLogicalOrder(a, b, true));
      }
    } else {
      // Use ascending or descending sort
      const isAscending = sortConfig.sortOrder === 'Ascending';
      return uniqueValues.sort((a, b) => compareWithLogicalOrder(a, b, isAscending));
    }
  };

  const RenderChart = () => {
    // Get sort configurations for x and y axes
    const xAxisSortConfig = sortConfigurations.find(
      (config: SortConfiguration) => config.fieldName === xAxisName,
    );
    const yAxisSortConfig = sortConfigurations.find(
      (config: SortConfiguration) => config.fieldName === yAxisName,
    );

    // Get sorted axis data
    const xAxisData = getSortedAxisValues(processedChartData, xAxisName, xAxisSortConfig);

    const yAxisData = getSortedAxisValues(processedChartData, yAxisName, yAxisSortConfig);

    return (
      <>
        <ReactEcharts
          ref={chartRef}
          onChartReady={handleChartReady}
          // theme={chartControl.colorScheme}
          style={{
            // padding: "1rem",
            width: graphDimension.width,
            height: graphDimension.height,
            overflow: 'hidden',
            margin: 'auto',
            border: chartArea ? 'none' : graphTileSize ? 'none' : '1px solid rgb(238,238,238)',
          }}
          option={{
            color: selectedTheme?.colors || [],
            backgroundColor: selectedTheme?.background || [],
            // animation: chartArea ? false : true, // Turning animation off for current release, to be added later on
            animation: false,
            legend: {
              // textStyle :{color : getContrastColor(chartThemes[0].background)},
              type: 'scroll',
              show:
                Math.floor(graphDimension.height) > LEGEND_THRESHOLDS.common.h
                  ? chartControl.legendOptions?.showLegend
                  : false,
              itemHeight: chartControl.legendOptions?.symbolHeight,
              itemWidth: chartControl.legendOptions?.symbolWidth,
              itemGap: chartControl.legendOptions?.itemGap,

              left: chartControl.legendOptions?.position?.left,
              top:
                chartControl.legendOptions?.position?.top !== 'bottom'
                  ? getTopMarginForLegend()
                  : null,
              // top: chartControl.legendOptions?.position?.top,
              bottom: chartControl.legendOptions?.position?.top === 'bottom' ? 0 : null,
              orient: chartControl.legendOptions?.orientation,
              textStyle: {
                color: selectedTheme?.dark ? '#ffffff' : palette.primary.contrastText,
                fontFamily: fontFamily,
              },
            },
            grid: {
              left:
                Math.floor(graphDimension.height) <= MARGIN_THRESHOLDS.compact.h
                  ? 5
                  : chartControl.chartMargin.left + 5 + '%',
              right:
                Math.floor(graphDimension.height) <= MARGIN_THRESHOLDS.compact.h
                  ? 5
                  : chartControl.chartMargin.right + '%',
              top:
                Math.floor(graphDimension.height) <= MARGIN_THRESHOLDS.compact.h
                  ? 5
                  : chartControl.legendOptions?.position?.top === 'top'
                    ? chartControl.chartMargin.top + 15 + '%'
                    : chartControl.chartMargin.top + '%',
              bottom:
                Math.floor(graphDimension.height) <= MARGIN_THRESHOLDS.compact.h
                  ? 5
                  : chartControl.legendOptions?.position?.top === 'bottom'
                    ? (graphDimension.height * chartControl.chartMargin.bottom) / 100 + 40
                    : chartControl.chartMargin.bottom + '%',
            },
            tooltip: {
              show: isTooltipEnabled,
              useHTML: true,
              trigger: 'item',
              backgroundColor: 'white',
              borderWidth: 1,
              padding: 0,
              textStyle: {
                color: '#333',
                fontSize: 16,
              },
              appendToBody: true,
              extraCssText:
                'box-shadow: 0 2px 8px rgba(0,0,0,0.15);white-space: normal; word-break: break-word;',
              formatter: function (params: any) {
                let tempData = params.data;

                if (Array.isArray(tempData)) {
                  const [xIndex, yIndex, value] = tempData;

                  tempData = {
                    [chartDataKeys[0]]: xAxisData[xIndex],
                    [chartDataKeys[1]]: yAxisData[yIndex],
                    [chartDataKeys[2]]: value,
                  };
                }

                const dimensionAxis1 = chartAxes[1];
                const dimensionAxis2 = chartAxes[2];
                const dimension1Value = tempData[dimensionAxis1.fields[0].displayname as string];
                const dimension2Value = tempData[dimensionAxis2.fields[0].displayname as string];
                const _data: { [key: string]: string | number } = {};

                //----------remove unchecked measure or any other field value of type date inside tooltip----------//
                Object.keys(tempData).forEach((key: string) => {
                  const resp = checkUidPresenceInCheckedMeasureForTooltip(
                    chartAxes,
                    key,
                    tooltipFormats?.checkedMeasure,
                  );
                  if (resp) {
                    _data[key] = tempData[key];
                  }
                });

                let mergedData = { ..._data };
                //-- send extra responses in _data so that it maps and shows all key value pairs including extra measure (if any)--//
                const extraResponses = tooltipFormats.extraMeasureFieldsResponses;
                if (tooltipFormats && extraResponses.length > 0) {
                  const extraFieldObj = extraResponses.find(
                    (obj: any) =>
                      String(obj[dimensionAxis1.fields[0].displayname as string]) ===
                        String(dimension1Value as string) &&
                      String(obj[dimensionAxis2.fields[0].displayname as string]) ===
                        String(dimension2Value as string),
                  );
                  if (extraFieldObj) {
                    //---- Remove all dimension keys from extraFieldObj ----//
                    const {
                      [dimensionAxis1.fields[0].displayname as string]: v1,
                      [dimensionAxis2.fields[0].displayname as string]: v2,
                      ...restExtraFields
                    } = extraFieldObj;
                    //-----remove unchecked measure field after adding extra measure ----//
                    let filteredFields = { ...mergedData, ...restExtraFields };
                    const measureKey = chartAxes.find((axis: any) => axis.name === 'Measure')
                      .fields[0].displayname;
                    const isPresent = checkUidPresenceInCheckedMeasureForTooltip(
                      chartAxes,
                      measureKey,
                      tooltipFormats?.checkedMeasure,
                    );
                    if (!isPresent) {
                      delete filteredFields[measureKey];
                    }
                    //---------send final response in tooltip data i.e checked measure plus extra fields-------//
                    mergedData = { ...filteredFields };
                  }
                }
                const dimensionDisplayNames = [
                  ...dimensionAxis1?.fields,
                  ...dimensionAxis2?.fields,
                ].map((field: any) => field.displayname);

                return tooltipStyle(
                  params.color,
                  mergedData,
                  tooltipFormats,
                  chartAxes,
                  dimensionDisplayNames,
                  undefined,
                  undefined,
                  fontFamily,
                );
              },
            },

            dataset: {
              source: processedChartData,
            },
            xAxis: {
              type: 'category',
              data: xAxisData,
              position: chartControl.axisOptions.xAxis[1].position,

              axisLine: {
                show:
                  chartControl.axisOptions.xAxis[1].position !== 'hide' &&
                  Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h,
                onZero:
                  chartControl.axisOptions.xAxis[1].position === 'top'
                    ? false
                    : chartControl.axisOptions.xAxis[1].onZero,
              },

              axisTick: {
                show:
                  chartControl.axisOptions.xAxis[1].position !== 'hide' &&
                  Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h,
                alignWithLabel: true,
                length:
                  chartControl.axisOptions.xAxis[1].position === 'top'
                    ? chartControl.axisOptions.xAxis[1].tickSizeTop
                    : chartControl.axisOptions.xAxis[1].tickSizeBottom,
              },
              axisLabel: {
                show:
                  chartControl.axisOptions.xAxis[1].position !== 'hide' &&
                  Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h,
                fontSize: chartControl.axisOptions.xAxis[1].fontSize,
                rotate:
                  chartControl.axisOptions.xAxis[1].position === 'top'
                    ? chartControl.axisOptions.xAxis[1].tickRotationTop
                    : chartControl.axisOptions.xAxis[1].tickRotationBottom,
                margin:
                  chartControl.axisOptions.xAxis[1].position === 'top'
                    ? chartControl.axisOptions.xAxis[1].tickPaddingTop
                    : chartControl.axisOptions.xAxis[1].tickPaddingBottom,
                color: selectedTheme?.dark ? '#ffffff' : palette.primary.contrastText,
                fontFamily: fontFamily,
                formatter: (value: any) => {
                  const fieldInAxes = chartAxes
                    ?.flatMap((ax: any) => ax.fields || [])
                    .find((f: any) => f.displayname === xAxisName);
                  const measureUid = fieldInAxes?.uId;

                  const labelFormat = chartControl?.formatOptions?.labelFormats?.measureFormats?.[
                    measureUid
                  ] as any;

                  if (labelFormat?.finalDateFormat) {
                    return formatChartXAxisValue(chartControl, value, labelFormat);
                  }

                  return value;
                },
              },
              splitLine: {
                show: chartControl.axisOptions.xSplitLine ?? false,
                lineStyle: {
                  color: chartControl.axisOptions.xAxis[1]?.splitLineColor || '#d3d3d3',
                  width: chartControl.axisOptions.xAxis[1]?.splitLineWidth ?? 1,
                  type: chartControl.axisOptions.xAxis[1]?.splitLineType || 'solid',
                },
              },

              show: true,

              name:
                Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h
                  ? chartControl.axisOptions.xAxis[1].name
                  : '',
              nameLocation: chartControl.axisOptions.xAxis[1].nameLocation,
              nameGap: chartControl.axisOptions.xAxis[1].nameGap,
              nameTextStyle: {
                fontSize: chartControl.axisOptions.xAxis[1].nameSize,
                color: chartControl.axisOptions.xAxis[1].nameColor,
              },
            },
            yAxis: {
              type: 'category',
              data: yAxisData,
              inverse: chartControl.axisOptions.inverse,

              position: chartControl.axisOptions.yAxis[1].position,

              axisLine: {
                show:
                  chartControl.axisOptions.yAxis[1].position !== 'hide' &&
                  Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h,
                onZero: chartControl.axisOptions.yAxis[1].onZero,
              },

              axisTick: {
                show:
                  chartControl.axisOptions.yAxis[1].position !== 'hide' &&
                  Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h,
                alignWithLabel: true,
                length:
                  chartControl.axisOptions.yAxis[1].position === 'left'
                    ? chartControl.axisOptions.yAxis[1].tickSizeLeft
                    : chartControl.axisOptions.yAxis[1].tickSizeRight,
              },

              axisLabel: {
                show:
                  chartControl.axisOptions.yAxis[1].position !== 'hide' &&
                  Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h,
                fontSize: chartControl.axisOptions.yAxis[1].fontSize,
                rotate:
                  chartControl.axisOptions.yAxis[1].position === 'left'
                    ? chartControl.axisOptions.yAxis[1].tickRotationLeft
                    : chartControl.axisOptions.yAxis[1].tickRotationRight,
                margin:
                  chartControl.axisOptions.yAxis[1].position === 'left'
                    ? chartControl.axisOptions.yAxis[1].tickPaddingLeft
                    : chartControl.axisOptions.yAxis[1].tickPaddingRight,
                color: selectedTheme?.dark ? '#ffffff' : palette.primary.contrastText,
                fontFamily: fontFamily,
                formatter: (value: number) => {
                  const fieldInAxes = chartAxes
                    ?.flatMap((ax: any) => ax.fields || [])
                    .find((f: any) => f.displayname === yAxisName);

                  const measureUid = fieldInAxes?.uId;
                  const labelFormat = chartControl?.formatOptions?.labelFormats?.measureFormats?.[
                    measureUid
                  ] as any;
                  if (labelFormat?.finalDateFormat) {
                    return formatChartYAxisValue(chartControl, value, labelFormat);
                  }
                  return value;
                },
              },
              splitLine: {
                show: chartControl.axisOptions.ySplitLine ?? true,
                lineStyle: {
                  color: chartControl.axisOptions.yAxis[1]?.splitLineColor || '#d3d3d3',
                  width: chartControl.axisOptions.yAxis[1]?.splitLineWidth ?? 1,
                  type: chartControl.axisOptions.yAxis[1]?.splitLineType || 'solid',
                },
              },

              show: true,

              name:
                Math.floor(graphDimension.height) > AXIS_THRESHOLDS.common.h
                  ? chartControl.axisOptions.yAxis[1].name
                  : '',
              nameLocation: chartControl.axisOptions.yAxis[1].nameLocation,
              nameGap: chartControl.axisOptions.yAxis[1].nameGap,
              nameTextStyle: {
                fontSize: chartControl.axisOptions.yAxis[1].nameSize,
                color: chartControl.axisOptions.yAxis[1].nameColor,
              },
            },
            visualMap: [
              {
                type: chartControl.calendarStyleOptions.pieceWise ? 'piecewise' : null,
                show:
                  Math.floor(graphDimension.height) > LEGEND_THRESHOLDS.heatmap.h
                    ? chartControl.legendOptions?.showLegend
                    : false,
                itemHeight: chartControl.calendarStyleOptions.pieceWise
                  ? chartControl.calendarStyleOptions?.height // in piecewise, height is coming correctly
                  : chartControl.calendarStyleOptions?.width, // if not piecewise, height is coming as width so modified like this
                itemWidth: chartControl.calendarStyleOptions.pieceWise
                  ? chartControl.calendarStyleOptions?.width // in piecewise, width is coming correctly
                  : chartControl.calendarStyleOptions?.height, // if not piecewise, width is coming as width so modified like this
                itemGap: chartControl.legendOptions?.itemGap,

                left: chartControl.legendOptions?.position?.left,
                top: chartControl.legendOptions?.position?.top,
                orient: chartControl.calendarStyleOptions?.orientation,

                min:
                  chartControl.colorScale.colorScaleType === 'Manual' &&
                  isValidNumber(chartControl.colorScale.min)
                    ? Number(chartControl.colorScale.min)
                    : minValue,

                max:
                  chartControl.colorScale.colorScaleType === 'Manual' &&
                  isValidNumber(chartControl.colorScale.max)
                    ? Number(chartControl.colorScale.max)
                    : maxValue,

                // TODO: Priority 1 - This property breaks page when switching from other chart types
                inRange: {
                  color: chartControl.colorScale.manualColor
                    ? [chartControl.colorScale.minColor, chartControl.colorScale.maxColor]
                    : selectedTheme?.colors?.length >= 2
                      ? [selectedTheme.colors?.[0], selectedTheme.colors?.[1] || '#ffffff']
                      : ['#000000', '#ffffff'],
                },
              },
            ],

            series: [
              {
                type: 'heatmap',
                // Convert data to the format: [xIndex, yIndex, value]
                data: processedChartData.map((item) => {
                  const xIndex = xAxisData.indexOf(item[xAxisName]);
                  const yIndex = yAxisData.indexOf(item[yAxisName]);
                  const value = item[chartDataKeys[2]];
                  return [xIndex, yIndex, value];
                }),
                emphasis: {
                  focus: 'self',
                },
                blur: {
                  itemStyle: {
                    opacity: 0.5,
                  },
                },
                label: {
                  show: chartControl.labelOptions.showLabel,
                  formatter: (params: any) => {
                    if (chartDataKeys.length > 0) {
                      // Get the actual data item from coordinates
                      const xValue = xAxisData[params.data[0]];
                      const yValue = yAxisData[params.data[1]];
                      const dataItem = processedChartData.find(
                        (item) => item[xAxisName] === xValue && item[yAxisName] === yValue,
                      );

                      if (dataItem) {
                        var formattedValue = dataItem[chartDataKeys[2]];
                        formattedValue = formatChartLabelValueForSelectedMeasure(
                          chartControls.properties[propKey],
                          chartProperties.properties[propKey],
                          formattedValue,
                          chartProperties.properties[propKey].chartAxes[
                            chartProperties.properties[propKey].chartAxes.findIndex(
                              (item: any) => item.name === 'Measure',
                            )
                          ]?.fields[0]?.displayname || '',
                        );
                        return formattedValue;
                      }
                    }
                    return '';
                  },
                  fontSize: chartControl.labelOptions.fontSize,
                  fontFamily: fontFamily,
                  color: chartControl.labelOptions.labelColor
                    ? chartControl.labelOptions.labelColor
                    : selectedTheme?.dark
                      ? '#ffffff'
                      : '#757474',
                  backgroundColor: chartControl.labelOptions.labelBackground
                    ? chartControl.labelOptions.labelBackgroundColor
                    : 'transparent',
                  borderRadius: chartControl.labelOptions.labelBackground ? 4 : 0,
                  padding: chartControl.labelOptions.labelBackground ? [4, 8] : 0,
                },
                itemStyle: {
                  borderColor: '#ccc',
                  borderWidth: 1,

                  // Add shadow to heatmap cells
                  shadowBlur: 8,
                  shadowColor: 'rgba(0, 0, 0, 0.4)',
                  shadowOffsetX: 2,
                  shadowOffsetY: 2,
                },
              },
            ],
          }}
        />
      </>
    );
  };

  const emptyQueryCondition =
    shouldChartDisplayWithSecurity(chartAxes, chartType, chartData) === false ||
    chartData.length < 1;

  return emptyQueryCondition ? (
    <EmptyQuery
      graphDimension={graphDimension}
      propKey={propKey}
      chartArea={chartArea}
      graphTileSize={graphTileSize}
      chartThemes={chartThemes}
      showEmptyQueryMessageWithSecurity={
        !shouldChartDisplayWithSecurity(chartAxes, chartType, chartData) ? true : false
      }
    />
  ) : (
    <RenderChart />
  );
};
const mapStateToProps = (state: ChartsMapStateToProps, ownProps: any) => {
  return {
    chartControls: state.chartControls,
    chartProperties: state.chartProperties,
  };
};
export default connect(mapStateToProps, null)(HeatMap);
